import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';

interface UserDashboardProps {
  isOpen: boolean;
  onClose: () => void;
}

// Normalized Cost Structure
const apps = [
  {
    id: 'opusgo',
    name: 'OpusGo',
    description: 'Retail Visualization Suite',
    icon: 'https://supoassets.s3.ap-south-1.amazonaws.com/public/sites/omniviewlabs/assets/icons/OpusGo.webp',
    url: 'https://opusgo.omniviewlabs.com',
    color: '#FF4D2D',
    costPerAction: '5 Credits / Render'
  },
  {
    id: 'opus3d',
    name: 'Opus3D',
    description: 'Enterprise Asset Manager',
    icon: 'https://supoassets.s3.ap-south-1.amazonaws.com/public/assets/icon/favicon.png',
    url: '#',
    color: '#003B5C',
    costPerAction: '200 Credits / Asset'
  },
  {
    id: 'opusstudio',
    name: 'OpusStudio',
    description: 'Interior Design Tool',
    icon: 'https://supoassets.s3.ap-south-1.amazonaws.com/public/sites/omniviewlabs/assets/icons/OpusInteriors.webp',
    url: 'https://studio.omniviewlabs.com/',
    color: '#6366F1',
    costPerAction: '100 Credits / Project'
  }
];

const UserDashboard: React.FC<UserDashboardProps> = ({ isOpen, onClose }) => {
  const { currentUser, userProfile, userWallet, logout, launchAppWithToken } = useAuth();
  const [activeView, setActiveView] = useState<'overview' | 'billing'>('overview');

  if (!isOpen || !currentUser) return null;

  // Visualization Helpers
  const planId = userProfile?.subscription.planId || 'explorer';
  const subCredits = userWallet?.balance_subscription || 0;
  const topupCredits = userWallet?.balance_topup || 0;

  // Progress Circle Logic (Reduced size)
  const maxSubCredits = planId === 'creator' ? 1000 : 50;
  const creditPercentage = Math.min((subCredits / maxSubCredits) * 100, 100);
  const circleCircumference = 2 * Math.PI * 32;
  const strokeDashoffset = circleCircumference - (creditPercentage / 100) * circleCircumference;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-[#021226]/80 backdrop-blur-md transition-opacity animate-reveal"
        onClick={onClose}
      />

      <div className="relative w-full max-w-5xl bg-white rounded-[2.5rem] shadow-[0_32px_128px_-16px_rgba(2,18,38,0.3)] overflow-hidden animate-reveal border border-slate-100 flex flex-col md:flex-row h-[85vh] md:h-[680px] transition-all duration-500">

        {/* --- Sidebar --- */}
        <div className="w-full md:w-64 bg-slate-50 border-r border-slate-100 p-6 flex flex-col">
          <div className="flex items-center gap-3 mb-8 px-2">
            <div className="w-10 h-10 rounded-xl bg-[#021226] text-white flex items-center justify-center font-black text-base shadow-lg">
              {(currentUser.displayName || currentUser.email || '?')[0].toUpperCase()}
            </div>
            <div className="overflow-hidden">
              <p className="text-sm font-black text-[#021226] truncate tracking-tight">{currentUser.displayName || 'User'}</p>
              <div className="flex items-center gap-1.5 mt-0.5">
                <span className="w-1.5 h-1.5 rounded-full bg-green-500"></span>
                <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">{planId} Member</p>
              </div>
            </div>
          </div>

          <nav className="space-y-1">
            <button
              onClick={() => setActiveView('overview')}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-xs font-black transition-all ${activeView === 'overview' ? 'bg-white text-[#021226] shadow-sm' : 'text-slate-400 hover:text-[#021226] hover:bg-white/50'}`}
            >
              <div className={`p-1 rounded-md ${activeView === 'overview' ? 'bg-[#021226] text-white' : 'bg-slate-200 text-slate-400'}`}>
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" /></svg>
              </div>
              Launchpad
            </button>
            <button
              onClick={() => setActiveView('billing')}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-xs font-black transition-all ${activeView === 'billing' ? 'bg-white text-[#021226] shadow-sm' : 'text-slate-400 hover:text-[#021226] hover:bg-white/50'}`}
            >
              <div className={`p-1 rounded-md ${activeView === 'billing' ? 'bg-[#021226] text-white' : 'bg-slate-200 text-slate-400'}`}>
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" /></svg>
              </div>
              Subscriptions
            </button>
          </nav>

          <div className="mt-auto pt-6 border-t border-slate-200/60">
            <button
              onClick={() => { logout(); onClose(); }}
              className="w-full flex items-center justify-between px-4 py-3 rounded-xl text-xs font-black text-red-500 hover:bg-red-50 transition-all"
            >
              Sign Out
              <svg className="w-4 h-4 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M17 16l4-4m0 0l-4-4m4 4H7" /></svg>
            </button>
          </div>
        </div>

        {/* --- Main Content Area --- */}
        <div className="flex-1 p-6 md:p-8 overflow-y-auto bg-white relative">

          <div className="flex justify-between items-end mb-6">
            <div>
              <h2 className="text-3xl font-black text-[#021226] tracking-tighter leading-none mb-1">
                Command Center
              </h2>
              <p className="text-xs font-bold text-slate-400 uppercase tracking-widest">
                {activeView === 'overview' ? 'Ready to Create' : 'Manage Subscriptions'}
              </p>
            </div>
            <button onClick={onClose} className="group p-2 rounded-full bg-slate-100 text-slate-400 hover:text-white hover:bg-[#021226] transition-all duration-300">
              <svg className="w-5 h-5 group-hover:rotate-90 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M6 18L18 6M6 6l12 12" /></svg>
            </button>
          </div>

          {activeView === 'overview' && (
            <div className="space-y-6 animate-reveal">
              {/* --- Refined Wallet Bar (Symmetrical Height) --- */}
              <div className="bg-[#021226] rounded-[1.5rem] p-6 text-white relative overflow-hidden shadow-2xl shadow-blue-900/20 group min-h-[180px] flex flex-col justify-center">
                <div className="absolute inset-0 bg-gradient-to-r from-[#021226] via-[#001E3C] to-[#021226] z-0"></div>
                <div className="absolute top-0 right-0 w-80 h-80 bg-[#FF5C00]/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/3 opacity-50 group-hover:opacity-100 transition-opacity duration-700"></div>
                <div className="absolute bottom-0 left-0 w-64 h-64 bg-[#003B5C]/20 rounded-full blur-3xl translate-y-1/2 -translate-x-1/3 pointer-events-none"></div>

                <div className="flex items-center justify-between relative z-10 px-2 h-full">
                  <div className="flex items-center gap-10">
                    {/* Main Balance */}
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <span className="relative flex h-2 w-2">
                          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#FF5C00] opacity-75"></span>
                          <span className="relative inline-flex rounded-full h-2 w-2 bg-[#FF5C00]"></span>
                        </span>
                        <span className="text-[10px] font-black uppercase tracking-[0.2em] text-[#FF5C00]">Total Balance</span>
                      </div>
                      <div className="flex items-baseline gap-3">
                        <h3 className="text-5xl font-black tracking-tighter tabular-nums text-white drop-shadow-lg">{userWallet?.total_available || 0}</h3>
                        <span className="text-xs font-bold text-slate-500 uppercase tracking-widest">OMNI</span>
                      </div>
                      <p className="text-[10px] text-slate-400 font-medium max-w-[180px] leading-tight mt-1 opacity-80">
                        Universal credits for all AI generation tasks.
                      </p>
                    </div>

                    <div className="hidden lg:block w-px h-16 bg-gradient-to-b from-transparent via-white/10 to-transparent"></div>

                    {/* Embellished Stats */}
                    <div className="hidden lg:flex gap-4">
                      {/* Subscription Credits */}
                      <div className="bg-white/5 backdrop-blur-md border border-white/5 rounded-2xl p-4 min-w-[140px] hover:bg-white/10 transition-colors">
                        <div className="flex items-center gap-2 mb-2">
                          <div className="p-1 rounded bg-[#021226] text-slate-300">
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                          </div>
                          <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Monthly</p>
                        </div>
                        <p className="text-2xl font-black tabular-nums leading-none mb-0.5">{subCredits}</p>
                        <p className="text-[9px] text-slate-500 font-medium">Refreshes {userProfile?.subscription?.nextBillingDate ? new Date(userProfile.subscription.nextBillingDate).toLocaleDateString(undefined, { day: 'numeric' }) : 'Monthly'}</p>
                      </div>

                      {/* Top-Up Credits */}
                      <div className="bg-white/5 backdrop-blur-md border border-white/5 rounded-2xl p-4 min-w-[140px] hover:bg-white/10 transition-colors">
                        <div className="flex items-center gap-2 mb-2">
                          <div className="p-1 rounded bg-[#021226] text-[#FF5C00]">
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                          </div>
                          <p className="text-[10px] font-black text-[#FF5C00] uppercase tracking-widest">Top-Up</p>
                        </div>
                        <p className="text-2xl font-black tabular-nums leading-none mb-0.5">{topupCredits}</p>
                        <p className="text-[9px] text-slate-500 font-medium">Never expires</p>
                      </div>
                    </div>
                  </div>

                  {/* Circular Usage */}
                  <div className="flex items-center gap-5 bg-black/20 pr-5 pl-2 py-2 rounded-[1.2rem] border border-white/5 backdrop-blur-sm hover:bg-black/30 transition-colors">
                    <div className="relative w-14 h-14 flex items-center justify-center">
                      <svg className="w-full h-full transform -rotate-90">
                        <circle cx="28" cy="28" r="22" stroke="currentColor" strokeWidth="4" fill="transparent" className="text-white/5" />
                        <circle
                          cx="28" cy="28" r="22"
                          stroke="#FF5C00" strokeWidth="4" fill="transparent"
                          strokeDasharray={2 * Math.PI * 22}
                          strokeDashoffset={(2 * Math.PI * 22) - (creditPercentage / 100) * (2 * Math.PI * 22)}
                          className="transition-all duration-1000 ease-out"
                          strokeLinecap="round"
                        />
                      </svg>
                      <div className="absolute inset-0 flex items-center justify-center">
                        <span className="text-[10px] font-black">{Math.round(creditPercentage)}%</span>
                      </div>
                    </div>
                    <div className="text-right hidden sm:block">
                      <p className="text-xs font-black leading-tight text-white">Monthly<br />Quota</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* --- App Modules Grid --- */}
              <div>
                <div className="flex items-center gap-3 mb-4 opacity-50">
                  <span className="text-[10px] font-black uppercase tracking-widest text-[#021226]">Applications</span>
                  <div className="h-px bg-[#021226] flex-1 opacity-20"></div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {apps.map((app) => {
                    const subMap = userProfile?.subscriptions || {};
                    let appPlan = subMap[app.id] || { planId: 'explorer', status: 'inactive' };
                    if (app.id === 'opusgo' && !subMap['opusgo'] && userProfile?.subscription) {
                      appPlan = userProfile.subscription;
                    }
                    const isActive = appPlan.status === 'active' && appPlan.planId !== 'explorer';

                    return (
                      <div key={app.id} className="group relative bg-white rounded-[1.5rem] p-5 border border-slate-100 shadow-sm hover:shadow-xl hover:shadow-blue-900/5 transition-all duration-300 hover:-translate-y-1 overflow-hidden">

                        <div className="absolute top-0 right-0 p-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                          <div className="w-2 h-2 rounded-full bg-[#021226] animate-ping"></div>
                        </div>

                        {isActive && (
                          <div className="absolute top-4 right-4 bg-green-500/10 text-green-600 px-2.5 py-1 rounded-full text-[8px] font-black uppercase tracking-widest">
                            Active
                          </div>
                        )}

                        <div className="flex flex-col h-full min-h-[160px]">
                          <div className={`w-14 h-14 rounded-2xl mb-4 flex items-center justify-center p-3 transition-colors duration-300 group-hover:bg-[#021226] group-hover:text-white`} style={{ backgroundColor: `${app.color}15` }}>
                            <img src={app.icon} alt={app.name} className="w-full h-full object-contain filter group-hover:brightness-0 group-hover:invert transition-all" />
                          </div>

                          <div>
                            <h4 className="text-xl font-black text-[#021226] tracking-tight mb-1">{app.name}</h4>
                            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">{app.costPerAction}</p>
                          </div>

                          <div className="mt-auto pt-6">
                            <button
                              onClick={() => launchAppWithToken(app.url)}
                              className="w-full flex items-center justify-between px-4 py-3 rounded-xl bg-slate-50 text-[#021226] text-[10px] font-black uppercase tracking-widest hover:bg-[#021226] hover:text-white transition-all duration-300 group-hover:shadow-lg"
                            >
                              <span>Launch</span>
                              <svg className="w-3 h-3 transform group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M14 5l7 7m0 0l-7 7m7-7H3" /></svg>
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {activeView === 'billing' && (
            <div className="animate-reveal max-w-4xl mx-auto py-2">
              <div className="space-y-3">
                {apps.map((app) => {
                  const subMap = userProfile?.subscriptions || {};
                  let appPlan = subMap[app.id] || { planId: 'explorer', status: 'inactive', nextBillingDate: undefined };
                  if (app.id === 'opusgo' && !subMap['opusgo'] && userProfile?.subscription) {
                    appPlan = userProfile.subscription;
                  }
                  const isActive = appPlan.status === 'active' && appPlan.planId !== 'explorer';

                  return (
                    <div key={app.id} className="bg-white rounded-2xl p-4 border border-slate-100 flex flex-col md:flex-row items-center justify-between shadow-sm hover:shadow-md transition-all group">
                      <div className="flex items-center gap-4 mb-3 md:mb-0">
                        <div className="w-12 h-12 bg-slate-50 rounded-xl flex items-center justify-center p-2 shrink-0 group-hover:scale-105 transition-transform" style={{ backgroundColor: `${app.color}08` }}>
                          <img src={app.icon} alt={app.name} className="w-full h-full object-contain" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2 mb-0.5">
                            <h3 className="text-base font-black text-[#021226] tracking-tighter">{app.name}</h3>
                            {isActive && <span className="w-1.5 h-1.5 rounded-full bg-green-500 shadow-sm shadow-green-200"></span>}
                          </div>
                          <div className="flex items-center gap-1.5">
                            <span className={`text-[8px] font-black uppercase tracking-widest px-2 py-0.5 rounded-md ${isActive ? 'bg-[#FF5C00]/10 text-[#FF5C00]' : 'bg-slate-100 text-slate-500'}`}>
                              {appPlan.planId}
                            </span>
                            <span className="text-[8px] text-slate-400 font-black uppercase tracking-widest italic">{isActive ? 'Recurring' : 'Free Tier'}</span>
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center gap-6">
                        <div className="hidden md:block text-right">
                          <p className="text-[8px] font-black text-slate-300 uppercase tracking-widest mb-0.5">Next Bill</p>
                          <p className="text-[10px] font-black text-[#021226]">
                            {appPlan.nextBillingDate ? new Date(appPlan.nextBillingDate).toLocaleDateString() : '—'}
                          </p>
                        </div>

                        <button
                          onClick={() => launchAppWithToken(app.url)}
                          className={`min-w-[120px] py-2.5 px-5 rounded-xl text-[9px] font-black uppercase tracking-widest transition-all ${isActive
                            ? 'bg-slate-100 text-[#021226] hover:bg-[#021226] hover:text-white'
                            : 'bg-[#FF5C00] text-white hover:bg-[#E65200] shadow-sm'
                            }`}
                        >
                          {isActive ? 'Launch App' : 'View Plans'}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="mt-8 p-6 bg-slate-50 rounded-[2rem] border border-dashed border-slate-200 flex flex-col md:flex-row items-center justify-between gap-4">
                <div className="text-center md:text-left">
                  <h4 className="text-base font-black text-[#021226] tracking-tight">Centralized Billing</h4>
                  <p className="text-[11px] text-slate-400 font-bold">Managed within each tool. Need help? Contact Support.</p>
                </div>
                <a href="mailto:support@omniviewlabs.com" className="px-6 py-3 bg-white border-2 border-slate-200 rounded-xl text-[9px] font-black uppercase tracking-widest text-[#003B5C] hover:border-[#021226] hover:text-[#021226] transition-all shadow-sm">
                  Contact Support
                </a>
              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  );
};

export default UserDashboard;